/**
 * Normalize GET_CONTENT-shaped data before Apollo writeQuery.
 * Inline PM tabContent rows omit __typename and may have partial image nodes;
 * writing them as-is triggers Apollo 3.14 cache errors in the browser console.
 */

function isRecord (value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function hasValidImageUrl (image: unknown): image is Record<string, unknown> {
  return isRecord(image) && typeof image.url === 'string' && image.url.length > 0
}

function sanitizeImageContentRelationships (value: unknown): unknown[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value
    .filter((entry) => isRecord(entry))
    .map((entry) => {
      const { image } = entry
      if (!hasValidImageUrl(image)) {
        return null
      }
      return {
        __typename: 'ImageContentRelationship',
        type: entry.type,
        image: {
          __typename: 'Image',
          url: image.url,
        },
      }
    })
    .filter(Boolean)
}

function sanitizeReference (ref: unknown): Record<string, unknown> | null {
  if (!isRecord(ref)) {
    return null
  }
  const { contentTo } = ref
  if (!isRecord(contentTo)) {
    return {
      __typename: 'ContentReference',
      ...ref,
    }
  }
  return {
    __typename: 'ContentReference',
    ...ref,
    contentTo: {
      __typename: 'Content',
      ...contentTo,
      imageContentRelationships: sanitizeImageContentRelationships(
        contentTo.imageContentRelationships,
      ),
    },
  }
}

function sanitizeContentItem (item: unknown): Record<string, unknown> | null {
  if (!isRecord(item)) {
    return null
  }
  const featureMedia = isRecord(item.featureMedia)
    ? { __typename: 'Media', ...item.featureMedia }
    : item.featureMedia
  const previewMedia = isRecord(item.previewMedia)
    ? { __typename: 'Media', ...item.previewMedia }
    : item.previewMedia
  const references = Array.isArray(item.references)
    ? item.references.map(sanitizeReference).filter(Boolean)
    : item.references
  return {
    __typename: 'Content',
    ...item,
    featureMedia,
    previewMedia,
    references,
    imageContentRelationships: sanitizeImageContentRelationships(item.imageContentRelationships),
  }
}

export default function prepareGetContentWriteQueryData (
  data: unknown,
): Record<string, unknown> | null {
  if (!isRecord(data)) {
    return null
  }
  const { contentList } = data
  if (!isRecord(contentList)) {
    return null
  }
  const rawContent = contentList.content
  if (!Array.isArray(rawContent) || !rawContent.length) {
    return null
  }
  const content = rawContent
    .map(sanitizeContentItem)
    .filter(Boolean) as Record<string, unknown>[]
  if (!content.length) {
    return null
  }
  return {
    contentList: {
      __typename: 'ContentListResponse',
      ...contentList,
      content,
    },
  }
}
